import { ColumnDef } from '@tanstack/react-table'
import { TextCell } from 'components/table/cells/TextCell'
import { format } from 'date-fns'
import { AssetCardProps } from 'features/assetDatabase/types/assetCardTypes'
import {
  AssetData,
  AssetTableEntries,
  AssetTableEntry,
  UnitAssetModelTable,
  UnitInspections,
  UnitPhotogrammetryModel,
} from 'features/assetDatabase/types/assetTableTypes'
import { CustomField } from 'features/assetDatabase/types/customField'
import React from 'react'
import { ForecastDarkTheme } from 'styles/themes'
import { InputType, RolodexEntryType } from 'types'
import { INSPECTION_TYPE_LABELS } from 'utils/inspectionTypes'
import { parseDate } from 'utils/parsingHelpers'

export const getCriticalityTagColor = (criticality: string) => {
  criticality = criticality?.toLowerCase()
  let color
  switch (criticality) {
    case 'low':
      color = ForecastDarkTheme.greenGlassBackground
      break
    case 'medium':
      color = ForecastDarkTheme.blueGlassBackground
      break
    case 'high':
      color = ForecastDarkTheme.redGlassBackground
      break
  }
  return color ?? ForecastDarkTheme.glassBackground
}

export const getAssetModels = (assetData: AssetTableEntries[] | undefined) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'asset-model')
    .map((unit) => {
      return {
        assetType: unit.entry.data.type,
        assetModelUnitTag: unit.entry.tags.unit,
      }
    })
}

export const assetInspections = (
  assetData: AssetTableEntries[] | undefined
) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'inspection')
    .map((inspection) => {
      return {
        inspectionSlug: inspection.entry.id,
        inspectionDate: inspection.entry.data.date,
        inspectionUnitTag: inspection.entry.tags.unit,
        inspectionType: inspection.entry.data.inspection_type,
      }
    })
}

export const assetPhotogrammetryModels = (
  assetData: AssetTableEntries[] | undefined
) => {
  return assetData
    ?.filter(
      (asset) => asset.entry.type === RolodexEntryType.PHOTOGRAMMETRY_MODEL
    )
    .map((photogrammetryModel) => {
      return {
        photogrammetryModelSlug: photogrammetryModel.entry.id,
        photogrammetryModelUnitTag: photogrammetryModel.entry.tags.unit,
      }
    })
}

export const getInspectionTypesForUnit = (
  unitInspections: UnitInspections[],
  unitId: string
): string[] => {
  const inspectionTypeValues = unitInspections
    .filter((inspection) => inspection.inspectionUnitTag === unitId)
    .map((inspection) => inspection.inspectionType)
    .filter((type) => type !== undefined)

  const uniqueInspectionTypes = [...new Set(inspectionTypeValues)]

  return uniqueInspectionTypes.map(
    (value) => INSPECTION_TYPE_LABELS[value] || value
  )
}

export const hasPhotogrammetryModelForUnit = (
  unitPhotogrammetryModels: UnitPhotogrammetryModel[] | undefined,
  unitId: string
): boolean => {
  if (!unitPhotogrammetryModels) return false

  return unitPhotogrammetryModels.some(
    (model) => model.photogrammetryModelUnitTag === unitId
  )
}

export const getInspectionTypesWithPhotogrammetryForUnit = (
  unitInspections: UnitInspections[],
  unitPhotogrammetryModels: UnitPhotogrammetryModel[] | undefined,
  unitId: string
): string[] => {
  const inspectionTypes = getInspectionTypesForUnit(unitInspections, unitId)

  if (hasPhotogrammetryModelForUnit(unitPhotogrammetryModels, unitId)) {
    return [...inspectionTypes, 'Photogrammetry']
  }

  return inspectionTypes
}

const findAssetModelType = (
  unitAssetModels: UnitAssetModelTable[] | undefined,
  unitId: string
) => {
  return unitAssetModels?.find((unit) => unit.assetModelUnitTag === unitId)
    ?.assetType
}

const getLatestInspection = (
  unitInspections: UnitInspections[] | undefined,
  unitTag: string
) => {
  const filteredInspections = unitInspections?.filter(
    (inspection) => inspection.inspectionUnitTag === unitTag
  )
  if (filteredInspections?.length === 0) {
    return undefined
  }
  if (filteredInspections?.length === 1) {
    return filteredInspections[0]
  }
  return filteredInspections?.reduce((prev, current) =>
    prev.inspectionDate &&
    current.inspectionDate &&
    prev.inspectionDate > current.inspectionDate
      ? prev
      : current
  )
}

export const convertDateToString = (
  dateInput: Date | undefined,
  formatString: string = 'EEE MMM dd yyyy'
): string | undefined => {
  if (dateInput !== undefined) {
    return format(dateInput, formatString)
  } else {
    return undefined
  }
}

const convertBlankToUndefined = (
  stringInput: string | undefined
): string | undefined => {
  if (stringInput?.trim() === '') {
    return undefined
  } else return stringInput?.trim()
}

// These props are all lists of the tagged unit IDs of the particular entry type,
// which will filtered & counted to display in the table for each unit
export const createAssetTableData = (
  unitAssets: AssetTableEntry[],
  unitAssetModels: UnitAssetModelTable[],
  unitComponents: string[],
  unitInspections: UnitInspections[],
  unitPhotogrammetryModels: UnitPhotogrammetryModel[],
  cardProps: AssetCardProps[],
  unitFiles: string[]
) => {
  const result: AssetData[] = unitAssets.map((unit) => {
    const lastInspection = getLatestInspection(unitInspections, unit.id)
    const inspectionTypes = getInspectionTypesWithPhotogrammetryForUnit(
      unitInspections,
      unitPhotogrammetryModels,
      unit.id
    )

    return {
      area: convertBlankToUndefined(unit.data.area),
      areaDescription: unit.data.area_description,
      assetModelType: findAssetModelType(unitAssetModels, unit.id),
      cardProps: cardProps.find((card) => card.unit.id === unit.id),
      criticality: convertBlankToUndefined(
        unit.data.custom_fields?.find(
          (field) => field.fieldName === 'criticality'
        )?.inputValue
      ),
      erpId: convertBlankToUndefined(unit.data.erp_id),
      id: unit.id,
      lastInspectionDate: parseDate(lastInspection?.inspectionDate),
      lastInspectionSlug: lastInspection?.inspectionSlug,
      lastUpdated: parseDate(unit.updated_at),
      name: unit.data.name.trim(),
      unitComponents:
        unitComponents?.filter((component) => component === unit.id).length ??
        0,
      unitPrimaryImage: unit.data.primary_image,
      customFields: unit.data.custom_fields,
      inspectionTypes,
      files: unitFiles.filter((file) => file === unit.id).length,
    }
  })
  return result
}

export const getAssetTableCustomColumns = (
  customFields: CustomField[]
): ColumnDef<AssetData>[] => {
  return customFields
    .filter(
      (field) =>
        field.key &&
        field.fieldName &&
        (field.inputType === InputType.Text ||
          field.inputType === InputType.Number ||
          field.inputType === InputType.Date ||
          field.inputType === InputType.MultiSelect) &&
        field.required !== undefined
    )
    .map((field) => {
      const columnDef: ColumnDef<AssetData> = {
        id: field.key,
        accessorKey: field.key,
        header: field.displayName || field.fieldName,
        enableSorting: true,
        meta: {
          inputType: field.inputType,
        },
        cell: ({ row }) => {
          const customFieldEntry = row.original.customFields?.find(
            (entry) => entry.fieldName === field.fieldName
          )
          const value = customFieldEntry?.inputValue
          return <TextCell truncate={true} text={value} />
        },
      }
      if (field.inputType === InputType.Date) {
        columnDef.filterFn = 'dateRangeFilter'
      } else if (field.inputType === InputType.MultiSelect) {
        columnDef.filterFn = 'multiSelectFilter'
      } else {
        columnDef.filterFn = 'includesString'
      }
      return columnDef
    })
}
